import { normalizeExperienceCompetencyName } from "./experienceIndexCategories";

const GENERIC_GAP_COPY = "This is one of the experiences with the most room to grow versus this comparison role.";
const GENERIC_STRENGTH_COPY = "This is one of the experiences where this person's completed answers stand out.";

// Third-person consultant dialect of membership-mobile-app Skills Index gap copy.
const COMPETENCY_GAP_COPY: Record<string, string> = {
  admin:
    "Tighter administrative systems would help this person's work hold up as responsibilities grow and free more time for the parts of the job they want to lead.",
  budget:
    "More direct budget ownership would give this person a clearer way to turn priorities into operating decisions and prove what those choices returned.",
  "financial acumen":
    "Stronger financial analysis would help this person evaluate what is working, make recommendations with more evidence, and turn sound instincts into decisions others can support.",
  operator:
    "Building more operating range would make the facility less dependent on heroics and create room for higher-level decisions when the pace picks up.",
  personnel:
    "More experience developing staff would make this person's systems less dependent on them doing the heavy lifting and help the team carry the work consistently.",
  network:
    "Growing a broader PGA network could bring in more benchmarks, ideas, and trusted help when larger decisions arrive.",
  "public speaking":
    "Developing a clearer executive voice could help this person turn what they know into influence in larger rooms and more formal settings.",
  "social media":
    "A stronger digital presence could help more members discover this person's work, keep pipelines active, and support steadier participation.",
  "teaching and coaching":
    "Building more coaching experience could help this person transfer what they know so others can own the work too, not only follow their process.",
  "golf academy":
    "The next opportunity is turning this person's reach into a repeatable academy or multi-week player-development program with a steadier pipeline.",
  technology:
    "More confidence with the systems behind the experience could help this person spot patterns, improve at scale, and give their team evidence instead of guesses.",
  "course maintenance":
    "More range in course and grounds planning would help this person connect playing conditions to the rest of the operation and speak to those tradeoffs with more confidence.",
  "food & beverage":
    "Building more food-and-beverage range would help this person connect hospitality to the rest of the member experience and make those service wins more repeatable.",
  merchandise:
    "Stronger merchandising range would help the shop support the member experience more consistently and tie product decisions to how the business actually performs.",
  "tournament operations":
    "More tournament ownership would help this person evaluate whether an event ran well, how it performed for the facility, and what to change next time."
};

// Third-person consultant dialect of membership-mobile-app Skills Index strength copy.
const COMPETENCY_STRENGTH_COPY: Record<string, string> = {
  admin:
    "This person keeps the details, deadlines, and follow-through organized so the rest of the operation can move with confidence.",
  budget:
    "This person connects spending choices to what the facility can actually support, and is comfortable making tradeoffs when resources compete.",
  "financial acumen":
    "This person sees how day-to-day decisions show up in the health of the business, and can connect activity to cost, revenue, and results.",
  operator:
    "This person keeps complex operations organized and moving when the calendar gets busy, and people can count on the day running as planned.",
  personnel:
    "This person brings structure to people work and appears comfortable helping teams stay connected, accountable, and ready to deliver the experience.",
  network:
    "This person creates momentum through relationships, and knows how to turn trust into collaboration, learning, and opportunity.",
  "public speaking":
    "This person knows how to bring people into an idea in the room, and can explain their work with enough clarity that others can act on it.",
  "social media":
    "This person knows how to make programs and work visible beyond the people already in front of them, and can create energy around participation.",
  "teaching and coaching":
    "This person's answers point to real teaching reps and a coach who can help players improve with clear, practical feedback.",
  "golf academy":
    "This person shows the building blocks of turning individual lessons into repeatable programs that can grow beyond one-off moments.",
  technology:
    "This person uses tools and systems to make feedback clearer, operations smoother, and their work easier to see and repeat.",
  "course maintenance":
    "This person respects both the condition of the facility and the planning required to support it, giving them a grounded view of how the operation holds together.",
  "food & beverage":
    "This person understands that a strong facility experience is built through service, and creates value in the spaces where members spend time together.",
  merchandise:
    "This person connects product, presentation, and the golf shop's role in the business in a way that can make retail a stronger part of the facility.",
  "tournament operations":
    "This person can organize event details, keep the day moving, and make the tournament calendar feel dependable for members and staff."
};

export function getExperienceIndexGapCopy(competencyName: string) {
  return COMPETENCY_GAP_COPY[normalizeExperienceCompetencyName(competencyName)] ?? GENERIC_GAP_COPY;
}

export function getExperienceIndexStrengthCopy(competencyName: string) {
  return COMPETENCY_STRENGTH_COPY[normalizeExperienceCompetencyName(competencyName)] ?? GENERIC_STRENGTH_COPY;
}
